import React from 'react'
import { ScrollRestoration, useLocation } from 'react-router'

const STORAGE_KEY = 'rr-scroll-positions'
const DEFAULT_RESTORE_KEY = 'default'

function persistCurrentScroll(key: string) {
  let value = {}

  try {
    value = JSON.parse(sessionStorage.getItem(STORAGE_KEY)!)
  } finally {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...value, [key]: window.scrollY }))
  }
}

/**
 * Some of our views are based on query params (unsupported by router),
 * which creates problems with scroll restore navigating between pages (eg. quote)
 *
 * In order to work around this, we need to only restore the scroll on page reload
 * and not on navigation. But at the same time, we need to write the scroll position,
 * so we can restore it if we need to.
 *
 * We can’t simply render the component conditionally, because it’d restore scroll
 * on mount, so we need a way of restoring scroll only on reload, while preserving
 * the saving functionality, which we do by switching the key in an effect after the component has rendered
 */
export function ScrollRestore() {
  const loc = useLocation()
  const resetKey = React.useMemo(() => {
    const key = loc.key === DEFAULT_RESTORE_KEY ? Math.random().toString(16) : loc.key

    // Save the current scroll position and restore from it on load if user scrolled anywhere
    if (window.scrollY !== 0) persistCurrentScroll(key)

    return key
  }, [loc.key])

  return <ScrollRestoration storageKey={STORAGE_KEY} getKey={() => resetKey} />
}
